"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const prop_types_1 = __importDefault(require("prop-types"));
const react_redux_1 = require("react-redux");
const every_1 = __importDefault(require("lodash/every"));
const forEach_1 = __importDefault(require("lodash/forEach"));
const isPlainObject_1 = __importDefault(require("lodash/isPlainObject"));
const map_1 = __importDefault(require("lodash/map"));
const isArray_1 = __importDefault(require("lodash/isArray"));
const mapValues_1 = __importDefault(require("lodash/mapValues"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const debounce_1 = __importDefault(require("lodash/debounce"));
const selectors_1 = require("@zedoc/selectors");
const DDPConnector_1 = __importDefault(require("./DDPConnector"));
const DDPProvider_1 = require("./DDPProvider");
const createResourcesSelectorFactory_1 = __importDefault(require("./selectors/createResourcesSelectorFactory"));
const utils_1 = require("./utils");
const uniqueId = (0, utils_1.createIdGenerator)('listener.');
const constant = (x) => () => x;
const increase = (key, value) => (prevState) => (Object.assign(Object.assign({}, prevState), { [key]: (prevState[key] || 0) + value }));
const createSubscriptionsSelector = (0, createResourcesSelectorFactory_1.default)('subscriptions');
const createQueriesSelector = (0, createResourcesSelectorFactory_1.default)('queries');
const ddp = (_a, ddpOptionsMore) => {
    var { subscriptions: makeMapStateToSubscriptions, queries: makeMapStateToQueries, selectors: createSelectors, messages = {}, mutations = {} } = _a, ddpOptions = __rest(_a, ["subscriptions", "queries", "selectors", "messages", "mutations"]);
    return (Inner) => {
        const { onMutationError, loader, renderLoader = (defaultComponent) => react_1.default.createElement(defaultComponent), queriesUpdateDelay, subscriptionsUpdateDelay, } = Object.assign(Object.assign({}, ddpOptions), ddpOptionsMore);
        if (!(0, isEmpty_1.default)(ddpOptionsMore)) {
            console.warn('Please specify ddp options in a single object only; ddp({}, {}) syntax is deprecated.');
        }
        const collection = prop_types_1.default.oneOfType([
            prop_types_1.default.objectOf(prop_types_1.default.any),
            prop_types_1.default.arrayOf(prop_types_1.default.any),
        ]);
        const propTypes = {
            ddpConnector: prop_types_1.default.instanceOf(DDPConnector_1.default).isRequired,
            requestedSubscriptions: collection,
            requestedQueries: collection,
            subscriptionsReady: prop_types_1.default.bool,
            queriesReady: prop_types_1.default.bool,
        };
        const defaultProps = {
            requestedSubscriptions: [],
            requestedQueries: [],
            subscriptionsReady: true,
            queriesReady: true,
        };
        class Container extends react_1.default.Component {
            constructor(props) {
                super(props);
                this.state = {
                    numberOfPendingMutations: 0,
                };
                const mutate = (request, options) => {
                    const { ddpConnector } = this.props;
                    if (request) {
                        const { name, params } = request;
                        this.beginMutation();
                        return ddpConnector
                            .apply(name, params, options)
                            .then((res) => this.endMutation(res))
                            .catch((err) => {
                            this.endMutation();
                            if (onMutationError) {
                                onMutationError(err);
                            }
                            else {
                                throw err;
                            }
                        });
                    }
                    return Promise.resolve();
                };
                this.handlers = {};
                (0, forEach_1.default)(mutations, (mutation, key) => {
                    this.handlers[key] = (...args) => mutation(Object.assign(Object.assign(Object.assign({}, this.props), this.handlers), { mutate }))(...args);
                });
                this.messageHandlers = {};
                (0, forEach_1.default)(messages, (handleMessage, key) => {
                    this.messageHandlers[key] = (...args) => handleMessage(this.props)(...args);
                });
                // NOTE: Since React 18, component can be mounted multiple times,
                //       so we want the identifier to be the same for all instances.
                this.id = uniqueId();
                this.unmounted = true;
            }
            componentDidMount() {
                const { ddpConnector, requestedSubscriptions, requestedQueries } = this.props;
                this.unmounted = false;
                this.updateSubscriptions = (0, debounce_1.default)((subscriptions) => {
                    if (!this.unmounted) {
                        ddpConnector.subsManager.updateRequests(this.id, subscriptions);
                    }
                }, subscriptionsUpdateDelay !== undefined
                    ? subscriptionsUpdateDelay
                    : ddpConnector.resourceUpdateDelay);
                this.updateQueries = (0, debounce_1.default)((queries) => {
                    if (!this.unmounted) {
                        ddpConnector.queryManager.updateRequests(this.id, queries);
                    }
                }, queriesUpdateDelay !== undefined
                    ? queriesUpdateDelay
                    : ddpConnector.resourceUpdateDelay);
                this.messagesListeners = (0, map_1.default)(this.messageHandlers, (messageHandler, channel) => ddpConnector.on(`messages.${channel}`, messageHandler));
                this.updateSubscriptions(requestedSubscriptions);
                this.updateQueries(requestedQueries);
            }
            componentDidUpdate() {
                const { requestedSubscriptions, requestedQueries } = this.props;
                this.updateSubscriptions(requestedSubscriptions);
                this.updateQueries(requestedQueries);
            }
            componentWillUnmount() {
                const { ddpConnector } = this.props;
                ddpConnector.subsManager.updateRequests(this.id, []);
                ddpConnector.queryManager.updateRequests(this.id, []);
                if (this.messagesListeners) {
                    this.messagesListeners.forEach((stop) => stop());
                    this.messagesListeners = null;
                }
                this.unmounted = true;
            }
            beginMutation() {
                if (!this.unmounted) {
                    this.setState(increase('numberOfPendingMutations', 1));
                }
            }
            endMutation(result) {
                if (!this.unmounted) {
                    this.setState(increase('numberOfPendingMutations', -1));
                }
                return result;
            }
            render() {
                const { numberOfPendingMutations } = this.state;
                const _a = this.props, { ddpConnector, subscriptionsReady, queriesReady, requestedSubscriptions, requestedQueries } = _a, other = __rest(_a, ["ddpConnector", "subscriptionsReady", "queriesReady", "requestedSubscriptions", "requestedQueries"]);
                const mutationsReady = numberOfPendingMutations <= 0;
                const Loader = loader === undefined ? ddpConnector.getLoaderComponent() : loader;
                if (Loader &&
                    renderLoader &&
                    (!subscriptionsReady || !mutationsReady || !queriesReady)) {
                    return renderLoader(Loader, Object.assign(Object.assign({}, other), { subscriptionsReady,
                        mutationsReady,
                        queriesReady }));
                }
                return react_1.default.createElement(Inner, Object.assign(Object.assign(Object.assign({}, other), this.handlers), { queriesReady,
                    mutationsReady,
                    subscriptionsReady }));
            }
        }
        Container.propTypes = propTypes;
        Container.defaultProps = defaultProps;
        if (process.env.NODE_ENV !== 'production') {
            Container.displayName = `ddp(${Inner.displayName})`;
        }
        const defaultValue = (x) => (y) => y || x;
        const setResourceId = (resource, id) => (Object.assign({ id }, resource));
        return (0, react_redux_1.connect)(() => {
            const selectRequestedSubscriptions = (0, utils_1.wrapMapState)(makeMapStateToSubscriptions);
            const selectRequestedQueries = (0, utils_1.wrapMapState)(makeMapStateToQueries);
            const selectSubscriptions = createSubscriptionsSelector(selectRequestedSubscriptions, setResourceId);
            const selectQueries = createQueriesSelector(selectRequestedQueries, setResourceId);
            const getSubscriptionsReady = (0, selectors_1.shallowEqualSelector)(selectSubscriptions, (subscriptions) => (0, every_1.default)(subscriptions, 'ready'));
            const getQueriesReady = (0, selectors_1.shallowEqualSelector)(selectQueries, (queries) => (0, every_1.default)(queries, 'ready'));
            const getQueriesValues = (0, selectors_1.shallowEqualSelector)(selectQueries, (queries) => {
                if ((0, isArray_1.default)(queries)) {
                    return {
                        queries: (0, map_1.default)(queries, 'value'),
                    };
                }
                return (0, mapValues_1.default)(queries, 'value');
            });
            //-----------------------------------------------
            const getSubscriptions = (0, selectors_1.deepEqualSelector)(selectRequestedSubscriptions, defaultValue([]));
            const getQueries = (0, selectors_1.deepEqualSelector)(selectRequestedQueries, defaultValue({}));
            const getOtherValues = createSelectors
                ? (0, selectors_1.toSelector)(createSelectors({
                    subscriptions: selectSubscriptions,
                    queries: selectQueries,
                }))
                : constant({});
            return (state, ownProps) => {
                const requestedSubscriptions = getSubscriptions(state, ownProps);
                const requestedQueries = getQueries(state, ownProps);
                const queriesValues = getQueriesValues(state, ownProps);
                return Object.assign(Object.assign(Object.assign({}, getOtherValues(state, ownProps)), ((0, isPlainObject_1.default)(queriesValues) && queriesValues)), { // it can be an array as well
                    requestedSubscriptions,
                    requestedQueries, subscriptionsReady: getSubscriptionsReady(state, ownProps), queriesReady: getQueriesReady(state, ownProps) });
            };
        })((props) => (react_1.default.createElement(DDPProvider_1.DDPContext.Consumer, null, (ddpConnector) => (react_1.default.createElement(Container
        // eslint-disable-next-line react/jsx-props-no-spreading
        , Object.assign({}, props, { ddpConnector: ddpConnector }))))));
    };
};
exports.default = ddp;
//# sourceMappingURL=ddp.js.map